import { useCallback, type KeyboardEvent as ReactKeyboardEvent } from 'react';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import type { ActionInputHandle } from '@bonusly/common-action-input';

import { useBizyContextualInsertStore, type BizyContextualSurface, type BizyContextualSurfaceData } from '../store/use-bizy-contextual-insert-store';
import { useBizySidePanelStore } from '../store/use-bizy-side-panel-store';

export interface UseBizyContextualSlashOptions {
	/** Which surface is invoking. Drives greeting copy and analytics. */
	surface: BizyContextualSurface;
	/** Turn the behavior off (e.g. feature flag off, or unsupported context). */
	enabled?: boolean;
	/**
	 * Read the current input handle. Called on every `/` keydown so the
	 * handle does not need to be stable at hook construction time.
	 */
	getInputHandle: () => ActionInputHandle | null;
	/**
	 * Called when `/` is consumed to capture recipient / context data that
	 * Bizy should know about. Keep this cheap — it runs on every invocation.
	 */
	getSurfaceData: () => BizyContextualSurfaceData;
	/** Short label describing recipient(s) for the greeting (e.g. "Alex"). */
	getRecipientLabel?: () => string | undefined;
	/**
	 * Fired immediately after the store is populated and the panel is told
	 * to open. Use to close host UI that would otherwise cover the Bizy
	 * panel (e.g. the give box modal).
	 */
	onInvoke?: () => void;
	/**
	 * Override the default dismiss behavior. The default refocuses the
	 * current input handle. Surfaces that unmount their input must provide
	 * this override (or leave it undefined) so we don't refocus a dead node.
	 */
	onDismiss?: () => void;
}

export interface UseBizyContextualSlashResult {
	/**
	 * Attach to an element that wraps (or is) the contenteditable input.
	 * Intercepts `/` as the first character in an empty input and opens
	 * Bizy contextually, consuming the keystroke so it never lands in the
	 * editor.
	 */
	handleKeyDownCapture: (event: ReactKeyboardEvent<HTMLElement>) => void;
}

/**
 * Shared behavior for "type `/` to summon Bizy with context" in supported
 * text inputs. The global `/` shortcut (`handleBizySlashKeyDown` in
 * `useBizyLayout`) intentionally ignores INPUT/TEXTAREA/contenteditable
 * targets, so surfaces that want to hijack `/` inside those inputs opt in
 * through this hook.
 *
 * Insert / Send-now actions on the resulting draft cards are handled by
 * the typed UI block dispatcher (`buildBizyUiBlockActionDispatcher`) —
 * not by this hook. The hook's only jobs are setting up the contextual
 * session in `useBizyContextualInsertStore` and opening the side panel.
 */
/**
 * Build a short, user-perspective starter message that gets auto-sent to
 * Bizy on open. This makes Bizy actively start chatting (asking the right
 * follow-up question) instead of waiting silently. The starter appears as
 * the user's first bubble — mirroring the "Request a feature" flow.
 *
 * The wording uses "Draft ... I can post/give" deliberately: without it,
 * Bizy treats "Help me reply to..." as a request to actually use its
 * `comment_on_bonus` / `give_recognition` tools, which blocks on things
 * like "can't comment on add-ons". In this flow Bizy is only drafting —
 * the user will insert the text into their own composer and post it.
 * The matching `bizy_contextual_mode: drafting` context field lets the
 * backend system prompt steer Bizy the same way.
 */
/**
 * Clean a Bonusly post body before stuffing it into a starter message:
 * markdown links collapse to their label text, bare URLs drop, and
 * runs of whitespace collapse. Without this, pasted Bonusly inbox links
 * (`[This](https://bonusly.com/a/inbox/...)`) bloat the starter and
 * push the meaningful sentence past the truncation point, which makes
 * Bizy lose grounding in the actual post.
 */
function cleanParentReason(reason: string): string {
	return reason
		.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
		.replace(/https?:\/\/\S+/g, '')
		.replace(/\s+/g, ' ')
		.trim();
}

function describeReceivers(surfaceData: BizyContextualSurfaceData): string | undefined {
	const names = surfaceData.receiverNames;
	if (!Array.isArray(names) || names.length === 0) return undefined;
	if (names.length === 1) return names[0];
	if (names.length === 2) return `${names[0]} and ${names[1]}`;
	return `${names[0]} and ${names.length - 1} others`;
}

function buildStarterMessage(surface: BizyContextualSurface, recipientLabel: string | undefined, surfaceData: BizyContextualSurfaceData): string {
	// Wording matters: the starter sets the agent's framing for the whole
	// session. Old wording was "Draft a recognition / comment for X." —
	// imperative, "go generate something". The agent took it literally
	// and drafted on turn one, robbing the giver of the chance to think
	// out loud about WHAT they actually want to say. New wording is
	// intent-first ("I want to...") + asks for help thinking it through,
	// which pairs with the agent prompt's "first turn = always ask" rule
	// to produce an interview-shaped session instead of a guess-shaped one.
	if (surface === 'recognition') {
		return recipientLabel
			? `I want to recognize ${recipientLabel} — help me figure out what to say.`
			: 'I want to recognize someone — help me figure out who and what to say.';
	}
	const rawReason = typeof surfaceData.parentReason === 'string' ? surfaceData.parentReason : '';
	const cleanedReason = cleanParentReason(rawReason);
	const truncatedReason = cleanedReason.length > 280 ? `${cleanedReason.slice(0, 277)}…` : cleanedReason;
	const receivers = describeReceivers(surfaceData);

	// Self-authored post: the user is following up on a recognition they
	// wrote. Don't suggest "thanking the author" (that's them); frame it
	// as a follow-up to the original receivers.
	if (surfaceData.currentUserIsAuthor) {
		const target = recipientLabel ? ` for ${recipientLabel}` : '';
		if (truncatedReason) {
			return `I want to add a follow-up comment to my own recognition${target} — help me figure out what to say. My original post: "${truncatedReason}"`;
		}
		return `I want to add a follow-up comment to my own recognition${target} — help me figure out what to say.`;
	}

	// Naming the action ("comment"), the giver, AND the receivers makes
	// the framing impossible to lose across turns. Earlier wording like
	// "reply on X's post" let Bizy drift into "do you mean a recognition
	// or a comment?" by turn 3 — keep the explicit "comment" anchor.
	if (recipientLabel && receivers && truncatedReason) {
		return `I want to comment on ${recipientLabel}'s recognition for ${receivers} — help me figure out what to add. ${recipientLabel} wrote: "${truncatedReason}"`;
	}
	if (recipientLabel && receivers) {
		return `I want to comment on ${recipientLabel}'s recognition for ${receivers} — help me figure out what to add.`;
	}
	if (recipientLabel && truncatedReason) {
		return `I want to comment on ${recipientLabel}'s post — help me figure out what to add. ${recipientLabel} wrote: "${truncatedReason}"`;
	}
	if (recipientLabel) {
		return `I want to comment on ${recipientLabel}'s post — help me figure out what to add.`;
	}
	return 'I want to comment on this post — help me figure out what to say.';
}

export function useBizyContextualSlash({
	surface,
	enabled = true,
	getInputHandle,
	getSurfaceData,
	getRecipientLabel,
	onInvoke,
	onDismiss,
}: UseBizyContextualSlashOptions): UseBizyContextualSlashResult {
	const setPendingContext = useBizyContextualInsertStore((state) => state.setPendingContext);
	const openPanel = useBizySidePanelStore((state) => state.open);
	const showChat = useBizySidePanelStore((state) => state.showChat);
	const { track } = useAnalytics();

	// Snapshot panel open-state at INVOKE time (read from `getState()` inside
	// the keydown handler, not from a subscription), so the dispatcher can
	// decide whether to close the panel on action completion. This needs to
	// reflect the panel state BEFORE `openPanel('contextual_slash')` runs.
	const getIsPanelOpen = () => useBizySidePanelStore.getState().isOpen;

	const handleKeyDownCapture = useCallback(
		(event: ReactKeyboardEvent<HTMLElement>) => {
			if (!enabled) return;
			if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;

			const handle = getInputHandle();
			if (!handle) return;

			// Only first-character in an empty input. V1 scope; mid-draft
			// invocation is a future consideration (see HABITS-1294).
			const currentText = handle.data().text ?? '';
			if (currentText.length > 0) return;

			// Consume the keystroke so `/` never lands in the contenteditable.
			// preventDefault() in the capture phase stops ProseMirror's
			// subsequent text input handling on modern browsers; if a stray
			// character ever slips through, we clear it defensively below.
			event.preventDefault();
			event.stopPropagation();

			const rawSurfaceData = getSurfaceData();
			const recipientLabel = getRecipientLabel?.();
			// `mode: drafting` tells the backend system prompt that Bizy is
			// drafting text for the user to insert — not acting on Bonusly
			// on their behalf. Without this, Bizy may try to call its
			// `comment_on_bonus` / `give_recognition` tools and hit
			// restrictions like "can't comment on add-ons".
			const surfaceData: BizyContextualSurfaceData = { ...rawSurfaceData, mode: 'drafting' };
			const starterMessage = buildStarterMessage(surface, recipientLabel, rawSurfaceData);
			const panelWasAlreadyOpen = getIsPanelOpen();

			setPendingContext({
				surface,
				surfaceData,
				recipientLabel,
				starterMessage,
				panelWasAlreadyOpen,
				onDismiss:
					onDismiss ??
					(() => {
						getInputHandle()?.focus();
					}),
			});

			track(AnalyticsEvent.Bizy.ContextualInvoked, {
				surface,
				path: window.location.pathname,
			});

			showChat();
			openPanel('contextual_slash');

			// Defensive: if a `/` did land before preventDefault took effect,
			// reset back to empty so the editor starts clean.
			requestAnimationFrame(() => {
				const after = getInputHandle();
				if (after && after.data().text === '/') {
					after.reset();
				}
			});

			// Fire after store is populated and panel is told to open so host
			// UI (e.g. the give box modal) can unmount without racing us.
			onInvoke?.();
		},
		[enabled, getInputHandle, getSurfaceData, getRecipientLabel, setPendingContext, surface, showChat, openPanel, track, onInvoke, onDismiss]
	);

	return { handleKeyDownCapture };
}
